import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, of, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as DailyLeadActions from './daily-lead.actions';
import { LeadsLegacyService } from '../../services/leads-legacy.service';
import { resetState } from '../global/global.actions';
import { selectIsLoggedIn } from '../auth/auth.selectors';
import { Store } from '@ngrx/store';

@Injectable()
export class DailyLeadLegacyEffects {

  loadDailyLeads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLeadActions.loadDailyLeads),
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      filter(([action, isLoggedIn]) => isLoggedIn),
      switchMap(([action]) =>
        this.leadsService.getLeads(action.params).pipe(
          map(response => DailyLeadActions.loadDailyLeadsSuccess({
            leads: response.data.data,
            meta: {
              current_page: response.data.current_page,
              from: response.data.from,
              last_page: response.data.last_page,
              per_page: response.data.per_page,
              to: response.data.to,
              total: response.data.total,
            },
          })),
          catchError(error => of(DailyLeadActions.loadDailyLeadsFailure({ error })))
        )
      )
    )
  );

  loadDailyLeadDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLeadActions.loadDailyLeadDetail),
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      filter(([action, isLoggedIn]) => isLoggedIn),
      switchMap(([action]) =>
        this.leadsService.getLeadDetails(action.id).pipe(
          map(response => DailyLeadActions.loadDailyLeadDetailSuccess({
            lead: response.data,
          })),
          catchError(error => of(DailyLeadActions.loadDailyLeadDetailFailure({ error })))
        )
      )
    )
  );

  generateDailyLeads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLeadActions.generateDailyLeads),
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      filter(([action, isLoggedIn]) => isLoggedIn),
      switchMap(([action]) =>
        this.leadsService.generateLeads(action.count, action.icp_id).pipe(
          map(response => DailyLeadActions.generateDailyLeadsSuccess({
            message: response.data.message,
            creditsDeducted: response.data.credits_deducted,
            estimatedLeads: response.data.estimated_leads,
          })),
          catchError(error => of(DailyLeadActions.generateDailyLeadsFailure({ error })))
        )
      )
    )
  );

  loadDailyLeadRuns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLeadActions.loadDailyLeadRuns),
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      filter(([action, isLoggedIn]) => isLoggedIn),
      switchMap(([action]) =>
        this.leadsService.getDailyLeadRuns(action.params).pipe(
          map(response => DailyLeadActions.loadDailyLeadRunsSuccess({
            runs: response.data,
            meta: response.meta,
          })),
          catchError(error => of(DailyLeadActions.loadDailyLeadRunsFailure({ error })))
        )
      )
    )
  );

  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLeadActions.loadNotifications),
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      filter(([action, isLoggedIn]) => isLoggedIn),
      switchMap(([action]) =>
        this.leadsService.getNotifications(action.params).pipe(
          map(response => DailyLeadActions.loadNotificationsSuccess({
            notifications: response.data.data,
            meta: {
              current_page: response.data.current_page,
              from: response.data.from,
              last_page: response.data.last_page,
              per_page: response.data.per_page,
              to: response.data.to,
              total: response.data.total,
            },
            unreadCount: response.metadata?.unread_count ?? 0,
          })),
          catchError(error => of(DailyLeadActions.loadNotificationsFailure({ error })))
        )
      )
    )
  );

  markAllNotificationsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DailyLeadActions.markAllNotificationsRead),
      withLatestFrom(this.store.select(selectIsLoggedIn)),
      filter(([action, isLoggedIn]) => isLoggedIn),
      switchMap(() =>
        this.leadsService.markAllNotificationsRead().pipe(
          switchMap(() => [
            DailyLeadActions.markAllNotificationsReadSuccess(),
            DailyLeadActions.loadNotifications({ params: {} }),
          ]),
          catchError(error => of(DailyLeadActions.markAllNotificationsReadFailure({ error })))
        )
      )
    )
  );

  cancelOnLogout$ = createEffect(() =>
      this.actions$.pipe(
        ofType(resetState),
        tap(() => {
          this.leadsService.cancelRequests();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private leadsService: LeadsLegacyService,
    private store: Store,
  ) {}
}
